import { useState } from 'react';
import {
  Box,
  Dialog,
  Drawer,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AppVersionLocale } from './types';

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  versionCode?: string;
  locales?: AppVersionLocale[];
  forceUpdate?: boolean;
  displayType?: DisplayType;
}

const LOCALE_LABELS: Record<string, string> = {
  ZH_HANS: '中文',
  EN: 'English',
};

type DisplayType = 'popup' | 'side';

export default function PreviewDialog({
  open,
  onClose,
  versionCode,
  locales,
  forceUpdate,
  displayType = 'side',
}: PreviewDialogProps) {
  const [selectedLocale, setSelectedLocale] = useState<string>('ZH_HANS');
  const locale = locales?.find((l) => l.locale === selectedLocale) ?? locales?.[0];
  const title = locale?.title || 'New Version';
  const notes = locale?.releaseNotes?.filter(Boolean) ?? [];
  const buttonText = locale?.button || 'Update Now';

  const localeSelector = locales && locales.length > 1 && (
    <ToggleButtonGroup
      value={selectedLocale}
      exclusive
      size="small"
      sx={{ alignSelf: 'center' }}
      onChange={(_, val) => {
        if (val) setSelectedLocale(val);
      }}
    >
      {locales.map((l) => (
        <ToggleButton key={l.locale} value={l.locale}>
          {LOCALE_LABELS[l.locale] ?? l.locale}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        gap: 1,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ flexShrink: 0 }}>
        {versionCode ? `${versionCode} — Preview` : 'Preview'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
        <IconButton onClick={onClose} size="small">
          ✕
        </IconButton>
      </Box>
    </Box>
  );

  const phoneFrame = (
    <Box
      sx={{
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          mx: 'auto',
          mb: 3,
          width: 400,
          height: 830,
          borderRadius: '24px',
          border: '10px solid #333',
          backgroundImage:
            selectedLocale === 'ZH_HANS'
              ? 'url(/images/mob-zh.png?v=1)'
              : 'url(/images/mob.png?v=1)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background blur overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
          }}
        />
        <Box
          sx={{
            height: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: '80%',
              background: 'rgba(255,255,255,0.5)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box sx={{ p: '16px 16px 16px' }}>
              {/* Title */}
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: '#111', pr: 12, lineHeight: 1.3 }}
              >
                {title}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#333', mb: 1.5 }}>
                {versionCode}
              </Typography>

              {/* Release notes */}
              {notes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#111', mb: 0.5 }}>
                    {selectedLocale === 'ZH_HANS' ? '更新内容：' : "What's new:"}
                  </Typography>
                  {notes.map((note, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        color: '#333',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 0.25,
                      }}
                    >
                      {notes.length > 1 ? `${i + 1}.${note}` : note}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#fff',
                  borderRadius: '32px',
                  border: '1.5px solid #e0e0e0',
                  p: '4px 44px 4px 4px',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#111',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 32,
                    lineHeight: 1,
                    flexShrink: 0,
                    pb: 0.5,
                  }}
                >
                  ›
                </Box>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{
                    color: '#111',
                    flex: 1,
                    textAlign: 'center',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {buttonText}
                </Typography>
              </Box>
            </Box>
            <Box
              position="absolute"
              zIndex={-1}
              top={0}
              left={0}
              component="img"
              src="/images/illustration.png?v=1"
              sx={{
                width: '100%',
                height: 160,
                objectFit: 'cover',
                objectPosition: 'center top',
                display: 'block',
                pointerEvents: 'none',
                select: 'none',
              }}
            />
          </Box>
          {!forceUpdate && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.8)',
                fontSize: 16,
              }}
            >
              ✕
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  if (displayType === 'side') {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        PaperProps={{ sx: { width: 480, display: 'flex', flexDirection: 'column' } }}
      >
        {header}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            items: 'center',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          {localeSelector}
          {phoneFrame}
        </Box>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', m: 2 } }}
    >
      {header}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          items: 'center',
          justifyContent: 'center',
          gap: 3,
          px: 5,
          py: 3,
        }}
      >
        {localeSelector}
        {phoneFrame}
      </Box>
    </Dialog>
  );
}
